// BRIEF 116 §3 — THE [play][clock] BLOCK, IN ONE PLACE, FOR EITHER PARENT KIND.
//
// ── WHY THIS FILE EXISTS ────────────────────────────────────────────────────
//
// §3 rules that an agenda event carries "the SAME [play][clock] block the task
// row carries — 115 §4's geometry verbatim". The word that decides the shape of
// this change is SAME. 115 §4 is the brief that had to go and re-measure the
// subtask cell because it had INVENTED its own geometry out of the same two
// buttons, and its ruling was: *a shared control's geometry is measured from
// its master, not invented*. Copying `tdTimer`'s twelve style properties into
// `CaseEventRow` and again into `AgendaView` would have produced three
// spellings of one control and a fourth brief to reconcile them — which is
// exactly the two-renderers defect 115 closed for rows (B.3).
//
// So the block becomes a component and the task row keeps rendering it, inside
// the `<td>` that is genuinely its own. The numbers below are not new: they are
// `tdTimer`'s, measured in the browser before this file existed and again
// after —
//
//     block 39 · play 20 · btnGap 1 · clock 18 · ink 8 · icons 12/12
//
// — which is `PLAY_W 20 + LOG_W 18 + 1` as `SubtaskChecklist` already spells it
// (115 §4). Three surfaces now, one anatomy, and a future change to it is one
// edit rather than a census.
//
// ── WHAT STAYS WITH THE HOST ────────────────────────────────────────────────
//
// The `<td>` stays in `CaseTaskRow`; the event card has no cell to speak of.
// (The C10/C83iv `reduced` size doubling that used to live on both sides was
// ruled out by 151E §2(a) — `reduced` is a layout variant, kept in the props
// for its callers, sizing nothing here any more.) The PREDICATE stays with the host too —
// a task hides the block when completed or archived, an event when archived —
// because "when is there work to time here" is a question about the row and not
// about the control.

import { Play, Clock } from "lucide-react";
import { I, fmtElapsed } from "../utils";
import { CT, RAD } from "../lib/tokens";
import SelfTick from "./SelfTick";

export type RowTimer = {
  paused?: boolean;
  elapsed?: number;
  startedAt?: number;
} | null | undefined;

export type RowTimerBlockProps = {
  /** This row's OWN timer row, already found by the host (a task-level timer
   *  for a task row, an event-level one for an event card). Absent = idle. */
  timer?: RowTimer;
  /** Play: start, resume, or (on the running one) pause. */
  onStart: () => void;
  /** Clock: open the Add-time-entry modal, prefilled by the host. */
  onLog: () => void;
  /** The clock's tooltip — the two kinds name different things. */
  logTitle?: string;
  /** C10/C83iv — the compact column's doubled icons. Task row only. */
  reduced?: boolean;
};

export function RowTimerBlock({
  timer,
  onStart,
  onLog,
  logTitle,
  reduced = false,
}: RowTimerBlockProps) {
  /* Round 15 (C92) — exactly the row OWNING the RUNNING timer shows the live
     elapsed readout; every other state keeps its triangle. A PAUSED timer
     renders as the triangle PLUS a static accumulated total, and the click
     resumes it. Moved here verbatim from `CaseTaskRow`'s `tdTimer`. */
  const isRunning = !!timer && !timer.paused;
  const isPaused = !!timer && !!timer.paused;
  return (
    <>
      <button
        className="btn"
        style={{
          padding: "1px 4px",
          /* 151E §2(a) — normal size in the reduced variant too; the
             C10/C83iv doubling is ruled out. */
          fontSize: isRunning ? 9 : 14,
          background: isRunning ? "var(--gs)" : "transparent",
          color: isRunning ? CT.c_fff : isPaused ? "var(--tm)" : "var(--tp)",
          border: "none",
          borderRadius: RAD.r4,
          cursor: "pointer",
          lineHeight: 1,
          fontFamily: isRunning ? "'JetBrains Mono',monospace" : "inherit",
          fontWeight: isRunning ? 600 : 400,
          display: "inline-flex",
          alignItems: "center",
          gap: 2,
        }}
        onClick={(e) => {
          /* 115 §1 — every interactive control on a row stops propagation:
             without it, starting a timer from the Dashboard (or from an event
             card, whose whole surface navigates) would also navigate. */
          e.stopPropagation();
          onStart();
        }}
      >
        {isRunning ? (
          <SelfTick active>
            {() =>
              fmtElapsed((timer!.elapsed || 0) + (Date.now() - (timer!.startedAt || 0)))
            }
          </SelfTick>
        ) : (
          <>
            <Play size={I.s} />
            {isPaused && (
              <span
                style={{
                  fontSize: 9,
                  fontFamily: "'JetBrains Mono',monospace",
                  fontWeight: 600,
                  color: "var(--tm)",
                }}
              >
                {fmtElapsed(timer!.elapsed || 0)}
              </span>
            )}
          </>
        )}
      </button>
      <button
        className="btn"
        style={{
          padding: "1px 3px",
          marginLeft: 1,
          background: "transparent",
          color: "var(--ts)",
          border: "none",
          borderRadius: RAD.r4,
          cursor: "pointer",
          lineHeight: 0,
        }}
        title={logTitle}
        onClick={(e) => {
          e.stopPropagation();
          onLog();
        }}
      >
        <Clock size={12} />
      </button>
    </>
  );
}

export default RowTimerBlock;
